import { useState } from "react";

import { Typography } from "@mui/material";
import { Controller, useForm } from "react-hook-form";

import CustomTextInput from "components/formComponents/CustomTextInput";
import CustomButton from "components/common/CustomButton";
import CustomCheckbox from "components/formComponents/CustomCheckbox";
import CustomPasswordInput from "components/formComponents/CustomPassword";
import { toast } from "sonner";
import { CustomTextWithO } from "components/common/CustomO";
import { Link, useNavigate } from "react-router-dom";
import Axios from "services/api/config";
import { loginURL } from "services/api/routes/Auth";
import { TOKEN_KEYS } from "utils/constant";
import userStore from "store/userStore";

interface FormData {
	email: string;
	password: string;
	remeberMe: boolean;
}

const LoginForm = () => {
	const navigate = useNavigate();
	const { setToken }: any = userStore();
	const [state, setState] = useState({
		loading: false,
	});
	const {
		control,
		handleSubmit,
		formState: { errors },
	} = useForm<FormData>();

	const onSubmit = async (data: FormData) => {
		try {
			setState((prev) => ({ ...prev, loading: true }));
			if (!data.email || !data.password)
				return toast.error("Please fill all necessary fields!");
			const { data: res }: any = await Axios({ ...loginURL, data });
			// Server short-circuits the OTP step in dev (admin.bypass-otp=true)
			// by returning the JWT directly. In that case, mirror the
			// verify-email success path: stash both tokens via setToken, skip
			// the /verify-email route. When OTP is enabled, requestId is
			// present and we route through OTP as before.
			if (res?.data?.token) {
				setToken({
					token: res.data.refreshToken,
					type: TOKEN_KEYS.REFRESH_TOKEN,
				});
				setToken({ token: res.data.token, type: TOKEN_KEYS.ACCESS_TOKEN });
			} else {
				navigate(`/verify-email`, {
					state: {
						email: data.email,
						requestId: res.data?.requestId,
					},
				});
			}
		} catch (error) {
			console.error(error);
		} finally {
			setState((prev) => ({ ...prev, loading: false }));
		}
	};

	return (
		<div className="md:w-1/2 flex justify-center items-center md:px-8 md:max-h-screen overflow-auto">
			<div className="p-8 flex flex-col justify-center items-center gap-4 text-black text-center">
				<div className="text-2xl md:text-4xl uppercase font-bold font-carmine">
					<CustomTextWithO text="Welcome" position={4} /> Back!
				</div>
				<Typography className="text-lg mb-2 text-center text-wrap">
					Manage your platform with ease. Log in to access your admin dashboard
					and stay in control.
				</Typography>

				<form onSubmit={handleSubmit(onSubmit)} className="w-full">
					{/* Signed out because an admin removed this person's help-desk access. */}
					{new URLSearchParams(window.location.search).get("removed") === "1" && (
						<Typography
							role="alert"
							className="!text-sm !mb-4 !px-3 !py-2 !rounded-lg"
							sx={{ bgcolor: "#FEF2F2", color: "#991B1B", border: "1px solid #FECACA" }}
						>
							Your help-desk access has been removed, so you have been signed out.
						</Typography>
					)}
					<div className="flex flex-col gap-4 w-full">
						{/* Company Name */}
						<Controller
							name="email"
							control={control}
							defaultValue=""
							// rules={{
							//   required: "Email is required",
							//   pattern: {
							//     value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
							//     message: "Invalid email address",
							//   },
							// }}
							render={({ field }) => (
								<CustomTextInput
									{...field}
									type="email"
									placeholder="Email"
									className="border-2 border-gray-400"
									autoFocus
									error={errors.email?.message}
								/>
							)}
						/>
						{/* Password */}
						<Controller
							name="password"
							control={control}
							defaultValue=""
							// rules={{
							//   required: "Password is required.",
							// }}
							render={({ field }) => (
								<CustomPasswordInput
									{...field}
									className="border-2 border-gray-400 rounded-md"
									placeholder="Password"
									error={errors.password?.message}
								/>
							)}
						/>
						<div>
							<div className="flex flex-wrap gap-3 justify-between items-center mb-4">
								{/* Terms and Policy */}
								<Controller
									name="remeberMe"
									control={control}
									render={({ field }) => (
										<CustomCheckbox
											{...field}
											checked={field.value}
											onChange={field.onChange}
											className="items-center"
											labelClassName="!text-sm !font-medium mt-1"
											label={<div>Keep me signed in.</div>}
											error={errors.remeberMe?.message}
										/>
									)}
								/>
								<Link to="/forgot-password">
									<div className="text-sm font-bold text-primary">
										Forgot Password?
									</div>
								</Link>
							</div>

							{/* Create Account Button */}
							<CustomButton
								title="Login"
								type="submit"
								variant="secondary"
								className="w-full"
								isLoading={state.loading}
								// isLoading={true}
							/>
						</div>
					</div>
				</form>

				{/* Login Link */}
				{/* <p className="text-sm mt-2 text-center text-black">
					New User?{" "}
					<Link to="/register" className="text-primary font-semibold">
						Create Account
					</Link>
				</p> */}
			</div>
		</div>
	);
};

export default LoginForm;
